import { useCallback, useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";

/**
 * Persistent floating "Somatic Audio Player" — plays the Casey's Cadence theme
 * with a play/pause toggle and a waveform that gently moves when playing.
 * Listens for a window "cadence:play" event (fired by the hero's audio CTA).
 */
export const SomaticPlayer = () => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const play = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = 0.55;
    a.play().then(() => setPlaying(true)).catch(() => {});
  }, []);

  const toggle = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) play();
    else { a.pause(); setPlaying(false); }
  }, [play]);

  useEffect(() => {
    const onPlay = () => play();
    window.addEventListener("cadence:play", onPlay);
    return () => window.removeEventListener("cadence:play", onPlay);
  }, [play]);

  return (
    <div
      className="fixed bottom-4 right-4 z-[80] flex items-center gap-3 rounded-full border border-[#AEE6D5]/30 bg-[#0A2E2C]/90 p-1.5 shadow-2xl backdrop-blur-2xl sm:pr-4 md:bottom-8 md:right-8 md:max-w-sm md:gap-4 md:rounded-2xl md:p-3"
      data-testid="somatic-player"
    >
      <audio ref={audioRef} src="/assets/cadence-main.mp3" loop preload="none" onEnded={() => setPlaying(false)} />

      <button
        onClick={toggle}
        aria-label={playing ? "Pause Casey's Cadence theme" : "Play Casey's Cadence theme"}
        data-testid="somatic-player-toggle"
        data-cursor="rhythm"
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#D4AF37] text-[#0A2E2C] transition-colors hover:bg-[#B99628]"
      >
        {playing ? <Pause className="h-5 w-5" /> : <Play className="ml-0.5 h-5 w-5" />}
      </button>

      {/* waveform — hidden on small phones to avoid covering content */}
      <div className="hidden h-6 items-end gap-[3px] sm:flex" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((i) => (
          <span
            key={i}
            className="w-[3px] rounded-full bg-[#AEE6D5]"
            style={{
              height: playing ? undefined : 5,
              animation: playing ? `cadenceBar 1s ${i * 0.12}s ease-in-out infinite` : "none",
            }}
          />
        ))}
      </div>

      <p className="hidden truncate font-mono text-[0.62rem] uppercase tracking-[0.12em] text-[#AEE6D5] md:block">
        Now Playing: Casey&#39;s Cadence Theme
      </p>

      <style>{`@keyframes cadenceBar{0%,100%{height:5px}50%{height:22px}}`}</style>
    </div>
  );
};
